import React, { useEffect, useState, useRef } from "react";
import { auleloApi } from "@/lib/auleloApi";
import { Button } from "@/components/ui/button";
import { Loader2, Mic2, Play, Pause, Download, Wand2 } from "lucide-react";
import { toast } from "sonner";

export default function AuleloPodcast() {
  const [digests, setDigests] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [audio, setAudio] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await auleloApi.get("/digest");
      setDigests(data || []);
      if (data?.length && !selectedId) setSelectedId(data[0].id);
    } catch {/* */}
  };
  useEffect(() => { load(); }, []);

  const render = async (digestId = null) => {
    setBusy(true);
    setAudio(null);
    try {
      const payload = digestId ? { digest_id: digestId } : {};
      const { data } = await auleloApi.post("/podcast", payload);
      const url = `data:audio/mpeg;base64,${data.audio_base64}`;
      setAudio({ url, digestId: data.digest_id, voice: data.voice, script: data.script });
      setSelectedId(data.digest_id);
      toast.success(`Rendered · ${data.voice}`);
      setTimeout(() => { if (ref.current) { ref.current.play(); setPlaying(true); } }, 100);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "TTS failed"); }
    finally { setBusy(false); }
  };

  const toggle = () => { if (!ref.current) return; if (playing) { ref.current.pause(); setPlaying(false); } else { ref.current.play(); setPlaying(true); } };

  const download = () => {
    if (!audio) return;
    const a = document.createElement("a");
    a.href = audio.url;
    a.download = `aulelo-${new Date().toISOString().slice(0,10)}.mp3`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div className="space-y-8" data-testid="aulelo-podcast-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-400">// audio cell</div>
          <h1 className="font-mono text-4xl md:text-5xl font-black tracking-tight mt-2 text-emerald-50">Morning podcast.</h1>
          <p className="text-zinc-400 mt-2 font-mono text-sm">Open AI TTS HD reads your daily brief in 90-120 seconds.</p>
        </div>
        <Button onClick={() => render(null)} disabled={busy} data-testid="aulelo-render-now-btn"
          className="rounded-none bg-emerald-500 hover:bg-emerald-400 text-black font-mono uppercase tracking-widest text-[11px] h-11 px-5">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
          Compile fresh & render
        </Button>
      </div>

      {audio && (
        <div className="border border-emerald-700/40 bg-black p-5" data-testid="aulelo-pod-player">
          <div className="flex items-center gap-4">
            <button onClick={toggle} data-testid="aulelo-pod-toggle"
              className="w-14 h-14 border border-emerald-500 bg-emerald-500/10 text-emerald-300 flex items-center justify-center hover:bg-emerald-500/20">
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>
            <div className="flex-1">
              <div className="font-mono text-xs uppercase tracking-[0.25em] text-emerald-300 flex items-center gap-2"><Mic2 className="w-3 h-3" /> Live cut</div>
              <div className="text-zinc-500 text-[10px] font-mono mt-0.5">voice: {audio.voice}</div>
            </div>
            <Button onClick={download} variant="outline" className="rounded-none border-emerald-700/40 bg-transparent text-emerald-200 hover:bg-emerald-500/10 font-mono uppercase tracking-widest text-[10px] h-9" data-testid="aulelo-pod-download">
              <Download className="w-3.5 h-3.5 mr-2" /> Download
            </Button>
          </div>
          <audio ref={ref} src={audio.url} onEnded={() => setPlaying(false)} controls className="w-full mt-4 [filter:invert(1)_hue-rotate(180deg)]" />
          {audio.script && (
            <details className="mt-4">
              <summary className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 cursor-pointer hover:text-emerald-300">// transcript</summary>
              <pre className="mt-2 text-zinc-400 text-[13px] whitespace-pre-wrap font-mono leading-relaxed">{audio.script}</pre>
            </details>
          )}
        </div>
      )}

      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">// past briefs (re-render any)</div>
        <div className="space-y-2">
          {digests.map((d) => (
            <div key={d.id} className="border border-zinc-800 bg-black p-4 flex items-center gap-3" data-testid={`aulelo-past-${d.id}`}>
              <div className="flex-1">
                <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-500">{String(d.created_at).slice(0, 16).replace("T", " · ")}</div>
                <div className="text-zinc-300 text-sm mt-1 line-clamp-2 font-mono">{(d.india || "").slice(0, 200)}</div>
              </div>
              <Button onClick={() => render(d.id)} disabled={busy} variant="outline" className="rounded-none border-emerald-700/40 bg-transparent text-emerald-200 hover:bg-emerald-500/10 font-mono uppercase tracking-widest text-[10px] h-9">
                <Mic2 className="w-3 h-3 mr-2" /> render
              </Button>
            </div>
          ))}
          {digests.length === 0 && (<div className="text-zinc-600 font-mono text-sm">No briefs yet — compile one from the briefing page first.</div>)}
        </div>
      </div>
    </div>
  );
}
